import {
  Component,
  Input,
  Output,
  EventEmitter,
  Injector,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { Options } from 'sortablejs';
import { FormBuilderService } from '../../../../core/services/form-builder.service';
import { FormBuilderV2StateService } from '../../services/form-builder-v2-state.service';
import { IFbPaletteItem } from '../../utils/fb-palette-catalog.util';
import { markFieldAsNew } from '../../utils/fb-new-field.util';
import { generateNewFieldKeyFromSettingsLabel } from '../../../../core/utils/form-fields-formly-configurations/form-fields-formly-settings/form-builder-default-settings-configuration-formly-fields.utils';
import { EIremboFormlyFieldTypes } from '@irembo-andela/irembogov3-common';

const REPEATER_TYPES = ['customrepeater', 'customdynamicrepeater'];

@Component({
  selector: 'irembogov-fb-block',
  templateUrl: './fb-block.component.html',
  styleUrls: ['./fb-block.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FbBlockComponent {
  @Input() block!: FormlyFieldConfig;
  @Input() sectionIndex!: number;
  @Input() blockIndex!: number;
  @Input() selectedFieldKey: string | null = null;
  @Output() fieldSelected = new EventEmitter<FormlyFieldConfig>();
  @Output() blockChanged = new EventEmitter<void>();
  @Output() deleteBlockEvent = new EventEmitter<number>();

  private formBuilderService: FormBuilderService;

  sortableOptions: Options = {
    group: {
      name: 'fields',
      pull: true,
      put: ['fields'],
    },
    animation: 200,
    dragClass: 'fb-sortable-drag',
    ghostClass: 'fb-sortable-ghost',
    chosenClass: 'fb-sortable-chosen',
    onEnd: () => this.onFieldReorder(),
    onAdd: (event: any) => this.onFieldAddedFromDrop(event),
  };

  repeaterSortableOptions: Options = {
    group: 'repeater-fields',
    animation: 200,
    dragClass: 'fb-sortable-drag',
    ghostClass: 'fb-sortable-ghost',
    chosenClass: 'fb-sortable-chosen',
    onEnd: () => this.onFieldReorder(),
  };

  constructor(
    private injector: Injector,
    private stateService: FormBuilderV2StateService,
    private cdr: ChangeDetectorRef
  ) {
    this.formBuilderService = this.injector.get(FormBuilderService);
  }

  get blockLabel(): string {
    return (this.block?.props?.['label'] as string) || 'Untitled Block';
  }

  get blockId(): string {
    return (
      this.block?.id ||
      this.block?.key?.toString() ||
      `block-${this.sectionIndex}-${this.blockIndex}`
    );
  }

  get isCollapsed(): boolean {
    return this.stateService.isCollapsed(this.blockId);
  }

  get fields(): FormlyFieldConfig[] {
    return this.block?.fieldGroup || [];
  }

  get fieldCount(): number {
    return this.fields.length;
  }

  // --- Repeater support ---

  isRepeaterField(field: FormlyFieldConfig): boolean {
    return REPEATER_TYPES.includes(field?.type?.toString() || '');
  }

  getRepeaterFieldGroup(field: FormlyFieldConfig): FormlyFieldConfig[] {
    const fa = field?.fieldArray as FormlyFieldConfig;
    if (!fa) return [];
    if (!fa.fieldGroup) fa.fieldGroup = [];
    return fa.fieldGroup;
  }

  onRepeaterFieldAdd(
    repeaterField: FormlyFieldConfig,
    newField: FormlyFieldConfig
  ): void {
    if (
      !repeaterField.fieldArray ||
      typeof repeaterField.fieldArray === 'function'
    ) {
      repeaterField.fieldArray = { fieldGroup: [] };
    }
    const fa = repeaterField.fieldArray as FormlyFieldConfig;
    if (!fa.fieldGroup) fa.fieldGroup = [];
    this.setFieldParent(newField, repeaterField);
    markFieldAsNew(newField);
    fa.fieldGroup.push(newField);
    this.fieldSelected.emit(newField);
    this.onFieldReorder();
  }

  onRepeaterFieldDelete(
    repeaterField: FormlyFieldConfig,
    childField: FormlyFieldConfig
  ): void {
    const fg = this.getRepeaterFieldGroup(repeaterField);
    const index = fg.indexOf(childField);
    if (index >= 0) {
      fg.splice(index, 1);
      this.onFieldReorder();
    }
  }

  onRepeaterFieldDuplicate(
    repeaterField: FormlyFieldConfig,
    childField: FormlyFieldConfig
  ): void {
    const fg = this.getRepeaterFieldGroup(repeaterField);
    const index = fg.indexOf(childField);
    if (index < 0) return;
    const clone = JSON.parse(JSON.stringify(childField));
    clone.key = `${childField.key}_copy_${Date.now()}`;
    fg.splice(index + 1, 0, clone);
    this.onFieldReorder();
  }

  isRepeaterCollapsed(field: FormlyFieldConfig): boolean {
    return this.stateService.isCollapsed(
      `repeater-${field.key?.toString() || field.id}`
    );
  }

  toggleRepeaterCollapse(field: FormlyFieldConfig): void {
    this.stateService.toggleCollapse(
      `repeater-${field.key?.toString() || field.id}`
    );
    this.cdr.markForCheck();
  }

  // --- Standard block operations ---

  toggleCollapse(): void {
    this.stateService.toggleCollapse(this.blockId);
    this.cdr.markForCheck();
  }

  onBlockLabelChange(newLabel: string): void {
    if (this.block.props) {
      this.block.props['label'] = newLabel;
    }
    this.blockChanged.emit();
  }

  onFieldSelect(field: FormlyFieldConfig): void {
    this.setFieldParent(field, this.block);
    this.fieldSelected.emit(field);
  }

  // Open this block's settings in the properties panel (same wiring as
  // selecting a field): the by-type config resolves block settings.
  configure(): void {
    this.fieldSelected.emit(this.block);
  }

  onFieldDelete(field: FormlyFieldConfig): void {
    if (field.expressions && Object.keys(field.expressions).length > 0) {
      const confirmed = confirm(
        'This field has expressions configured. Are you sure you want to delete it?'
      );
      if (!confirmed) return;
    }
    const fieldGroup = this.block.fieldGroup;
    if (!fieldGroup) return;
    const locationWidgetRootKey = this.getLocationWidgetRootKey(field);
    if (
      locationWidgetRootKey &&
      this.shouldDeleteLocationWidgetGroup(
        field,
        fieldGroup,
        locationWidgetRootKey
      )
    ) {
      this.block.fieldGroup = fieldGroup.filter(
        candidate =>
          candidate.key !== locationWidgetRootKey &&
          candidate.props?.['locationWidgetKey'] !== locationWidgetRootKey
      );
    } else {
      const idx = fieldGroup.findIndex(
        candidate => candidate.key === field.key
      );
      if (idx !== -1) {
        fieldGroup.splice(idx, 1);
      }
      this.removeLocationWidgetFieldReference(field, fieldGroup);
    }
    this.formBuilderService.configureFieldAction$.next(null);
    this.onFieldReorder();
    this.cdr.detectChanges();
  }

  onFieldDuplicate(field: FormlyFieldConfig): void {
    const fieldGroup = this.block.fieldGroup;
    if (!fieldGroup) return;

    const idx = fieldGroup.findIndex(f => f.key === field.key);
    if (idx === -1) return;

    // Deep copy the field
    const clone: FormlyFieldConfig = JSON.parse(JSON.stringify(field));

    const locationWidgetReferenceKeys = this.getLocationWidgetReferenceKeys(
      field,
      fieldGroup
    );
    if (locationWidgetReferenceKeys.length) {
      this.clearLocationWidgetMetadata(clone, locationWidgetReferenceKeys);
    }

    // Generate unique key and id
    const uid =
      Date.now().toString(36) + Math.random().toString(36).substr(2, 5);
    const baseKey = (clone.key?.toString() ?? '').replace(/[0-9]+$/, '');
    clone.key = `${baseKey}${uid}`;
    if (clone.id) {
      const baseId = clone.id.replace(/[0-9]+$/, '');
      clone.id = `${baseId}${uid}`;
    }
    clone.defaultValue = null;

    // Rename label to prevent duplicates
    if (clone.props?.label) {
      const originalLabel = field.props?.label ?? '';
      const re = new RegExp(
        `^${originalLabel.replace(
          /[.*+?^${}()|[\]\\]/g,
          '\\$&'
        )} \\(Copy \\d+\\)$`
      );
      const siblingCount = fieldGroup.filter(
        f => f.props?.label === originalLabel || re.test(f.props?.label ?? '')
      ).length;
      clone.props.label = `${originalLabel} (Copy ${siblingCount})`;
    }

    // Restore parent reference (lost during JSON clone)
    (clone as any).parent = field.parent;

    // A duplicate is a fresh field — let its label drive the key until saved.
    markFieldAsNew(clone);

    // Insert after the original — mutate the same array so sortablejs sees it
    fieldGroup.splice(idx + 1, 0, clone);

    this.formBuilderService.configureFieldAction$.next(clone);
    this.onFieldReorder();
    this.cdr.detectChanges();
  }

  private getLocationWidgetRootKey(
    field: FormlyFieldConfig
  ): FormlyFieldConfig['key'] | undefined {
    if (field.props?.['locationWidgetRoot']) return field.key;
    if (field.props?.['locationWidgetKey']) {
      return field.props['locationWidgetKey'];
    }
    if (
      field.type === EIremboFormlyFieldTypes.customcascadingdropdowns &&
      field.props?.['countryFieldKey']
    ) {
      return field.key;
    }
    return undefined;
  }

  private shouldDeleteLocationWidgetGroup(
    field: FormlyFieldConfig,
    fieldGroup: FormlyFieldConfig[],
    rootKey: FormlyFieldConfig['key']
  ): boolean {
    if (
      field.props?.['locationWidgetRoot'] ||
      (field.type === EIremboFormlyFieldTypes.customcascadingdropdowns &&
        field.key === rootKey)
    ) {
      return true;
    }
    const root = fieldGroup.find(candidate => candidate.key === rootKey);
    return root?.props?.['countryFieldKey'] === field.key;
  }

  private removeLocationWidgetFieldReference(
    field: FormlyFieldConfig,
    fieldGroup: FormlyFieldConfig[]
  ): void {
    const rootKey = field.props?.['locationWidgetKey'];
    if (!rootKey) return;
    const root = fieldGroup.find(candidate => candidate.key === rootKey);
    if (!root?.props) return;

    if (root.props['cityFieldKey'] === field.key) {
      delete root.props['cityFieldKey'];
      root.props['enableInternationalCity'] = false;
    }
    if (root.props['localLocationFieldKey'] === field.key) {
      delete root.props['localLocationFieldKey'];
      delete root.props['locationLastLevel'];
    }
  }

  private clearLocationWidgetMetadata(
    field: FormlyFieldConfig,
    referenceKeys: FormlyFieldConfig['key'][]
  ): void {
    if (!field.props) return;
    delete field.props['locationWidgetRoot'];
    delete field.props['locationWidgetKey'];
    delete field.props['countryFieldKey'];
    delete field.props['localLocationFieldKey'];
    delete field.props['cityFieldKey'];
    delete field.props['dragDisabled'];
    if (field.expressions) {
      Object.entries(field.expressions).forEach(([key, expression]) => {
        if (
          typeof expression === 'string' &&
          referenceKeys.some(referenceKey =>
            expression.includes(referenceKey?.toString() ?? '')
          )
        ) {
          delete field.expressions?.[key];
        }
      });
    }
  }

  private getLocationWidgetReferenceKeys(
    field: FormlyFieldConfig,
    fieldGroup: FormlyFieldConfig[]
  ): FormlyFieldConfig['key'][] {
    const rootKey = this.getLocationWidgetRootKey(field);
    if (!rootKey) return [];

    const root = fieldGroup.find(candidate => candidate.key === rootKey);
    return [rootKey, root?.props?.['countryFieldKey']].filter(
      (key): key is NonNullable<FormlyFieldConfig['key']> => key !== undefined
    );
  }

  onAddField(newField: FormlyFieldConfig): void {
    if (!this.block.fieldGroup) {
      this.block.fieldGroup = [];
    }
    this.setFieldParent(newField, this.block);
    markFieldAsNew(newField);
    this.block.fieldGroup.push(newField);
    this.fieldSelected.emit(newField);
    this.blockChanged.emit();
  }

  onDeleteBlock(): void {
    this.deleteBlockEvent.emit(this.blockIndex);
  }

  isFieldSelected(field: FormlyFieldConfig): boolean {
    return (
      this.selectedFieldKey !== null &&
      field.key?.toString() === this.selectedFieldKey
    );
  }

  trackByFieldKey(index: number, field: FormlyFieldConfig): string {
    return field.key?.toString() || field.id || `field-${index}`;
  }

  private onFieldAddedFromDrop(event: any): void {
    const addedItem = this.fields[event.newIndex];
    // A dropped palette item is an IFbPaletteItem (has 'config' + 'displayName').
    // Replace it with a clone of its V1 config, keyed from its label.
    if (addedItem && 'config' in addedItem && 'displayName' in addedItem) {
      const item = addedItem as unknown as IFbPaletteItem;
      const freshConfig: FormlyFieldConfig = JSON.parse(
        JSON.stringify(item.config)
      );
      generateNewFieldKeyFromSettingsLabel(
        freshConfig,
        this.formBuilderService
      );
      markFieldAsNew(freshConfig);
      this.setFieldParent(freshConfig, this.block);
      this.fields[event.newIndex] = freshConfig;
      this.fieldSelected.emit(freshConfig);
    }
    this.onFieldReorder();
    this.cdr.markForCheck();
  }

  private onFieldReorder(): void {
    for (const field of this.fields) {
      this.setFieldParent(field, this.block);
    }
    this.formBuilderService.refreshFormlyForm(false, true);
    this.formBuilderService.requestFormlyFieldsSave();
    this.blockChanged.emit();
  }

  private setFieldParent(
    field: FormlyFieldConfig | null | undefined,
    parent: FormlyFieldConfig
  ): void {
    if (!field) return;
    Object.defineProperty(field, 'parent', {
      value: parent,
      enumerable: false,
      writable: true,
      configurable: true,
    });
  }
}
